// FinanceStaffRequestPortal.jsx — Finance's view of Librarians/Hostel Members.
// Finance can only look and *request* changes — every add/exit/reactivate goes
// through finance_requests for Admin to approve (see financeAdminRoutes.js
// executeFinanceRequestAction's librarian_*/hostel_member_* branches). This is
// the "everything through request to admin" requirement, mirroring how Finance
// already requests asset/expense changes rather than writing directly.
import PasswordInput from '../components/PasswordInput';
import { createPortal } from 'react-dom';
import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';
import {
  PlusIcon, XMarkIcon, ArrowRightOnRectangleIcon, NoSymbolIcon, CheckCircleIcon,
  MagnifyingGlassIcon, LockClosedIcon, LockOpenIcon,
} from '@heroicons/react/24/outline';

const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}).replace(/ /g,'-') : '—';
const ini  = (n='') => n.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2)||'?';

const cssFor = (accent) => `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .fr*{box-sizing:border-box;} .fr{font-family:'Sora',sans-serif;color:#1a1d2e;animation:frFade .4s ease both;}
  @keyframes frFade{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  .fr-hdr{display:flex;align-items:center;justify-content:space-between;margin-bottom:1.75rem;flex-wrap:wrap;gap:12px;}
  .fr-title{font-size:1.6rem;font-weight:700;letter-spacing:-.03em;color:#0f1123;}
  .fr-sub{font-size:.78rem;color:#9094a8;margin-top:2px;}
  .btn{padding:8px 16px;border-radius:10px;font-size:.8rem;font-weight:600;font-family:'Sora',sans-serif;cursor:pointer;border:none;display:inline-flex;align-items:center;gap:7px;transition:.15s;}
  .btn svg{width:15px;height:15px;stroke-width:2;}
  .btn-accent{background:${accent};color:white;} .btn-accent:hover{filter:brightness(0.92);}
  .btn-ghost{background:#f0f2f7;color:#6b7194;border:1px solid #e0e4f0;} .btn-ghost:hover{background:#e8ebf4;}
  .btn-amber{background:#fffbeb;color:#d97706;border:1px solid #fde68a;} .btn-amber:hover{background:#d97706;color:white;}
  .btn-red{background:#fef2f2;color:#ef4444;border:1px solid #fecaca;} .btn-red:hover{background:#ef4444;color:white;}
  .btn-sm{padding:5px 11px;font-size:.74rem;} .btn:disabled{opacity:.5;cursor:not-allowed;}
  .panel{background:white;border-radius:16px;border:1px solid #e8ebf4;overflow:hidden;margin-bottom:1rem;}
  .panel-hdr{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:16px 20px;border-bottom:1px solid #f0f2f8;flex-wrap:wrap;}
  .tbl{width:100%;border-collapse:collapse;}
  .tbl thead tr{background:#f8f9fc;border-bottom:1px solid #eaecf4;}
  .tbl th{padding:11px 16px;text-align:center !important;font-size:.67rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.07em;white-space:nowrap;}
  .tbl tbody tr{border-bottom:1px solid #f3f5fb;} .tbl tbody tr:hover{background:#fafbff;}
  .tbl td{padding:11px 16px;font-size:.8rem;color:#3a3f58;vertical-align:middle;}
  .nc{display:flex;align-items:center;gap:10px;}
  .av{width:32px;height:32px;border-radius:9px;background:linear-gradient(135deg,${accent},#1a1d2e);display:flex;align-items:center;justify-content:center;font-size:.65rem;font-weight:700;color:white;flex-shrink:0;}
  .nm{font-weight:600;color:#1a1d2e;font-size:.82rem;} .ne{font-size:.68rem;color:#9094a8;margin-top:1px;}
  .mono{font-family:'JetBrains Mono',monospace;font-size:.74rem;color:#6b7194;}
  .pill{display:inline-flex;font-size:.67rem;font-weight:600;padding:2px 9px;border-radius:99px;}
  .pill.active{background:#ecfdf5;color:#10c98e;} .pill.resigned{background:#fef2f2;color:#ef4444;}
  .pill.terminated{background:#1a1d2e;color:white;} .pill.inactive{background:#f0f2f7;color:#9094a8;}
  .act-btn{width:30px;height:30px;border-radius:8px;border:1px solid #e8ebf4;background:#f8f9fc;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;margin-left:3px;}
  .act-btn svg{width:14px;height:14px;stroke-width:1.8;}
  .act-btn.resign{color:#d97706;} .act-btn.resign:hover{background:#fffbeb;border-color:#fde68a;}
  .act-btn.terminate{color:#ef4444;} .act-btn.terminate:hover{background:#fef2f2;border-color:#fecaca;}
  .act-btn.reactivate{color:#10c98e;} .act-btn.reactivate:hover{background:#ecfdf5;border-color:#10c98e;}
  .act-btn.deactivate{color:#ef4444;} .act-btn.deactivate:hover{background:#fef2f2;border-color:#fecaca;}
  .act-btn.activate{color:#10c98e;} .act-btn.activate:hover{background:#ecfdf5;border-color:#10c98e;}
  .empty{text-align:center;padding:48px;color:#b0b5cc;font-size:.82rem;}
  .spinner{width:16px;height:16px;border:2px solid #e4e7f0;border-top-color:${accent};border-radius:50%;animation:sp .7s linear infinite;}
  @keyframes sp{to{transform:rotate(360deg)}}
  .overlay{position:fixed;inset:0;background:rgba(10,12,28,.55);backdrop-filter:blur(3px);display:flex;align-items:center;justify-content:center;z-index:1000;padding:20px;}
  .modal{background:white;border-radius:20px;width:100%;max-height:90vh;border:1px solid #e8ebf4;box-shadow:0 24px 64px rgba(0,0,0,.18);overflow:hidden;display:flex;flex-direction:column;}
  .m-hdr{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #f0f2f8;flex-shrink:0;gap:8px;}
  .m-title{font-size:.95rem;font-weight:700;color:#0f1123;} .m-sub{font-size:.72rem;color:#9094a8;}
  .m-close{width:30px;height:30px;border-radius:8px;border:1px solid #e8ebf4;background:#f8f9fc;display:flex;align-items:center;justify-content:center;cursor:pointer;}
  .m-body{padding:22px;overflow-y:auto;flex:1;} .m-foot{display:flex;justify-content:flex-end;gap:8px;padding:16px 22px;border-top:1px solid #f0f2f8;flex-shrink:0;flex-wrap:wrap;}
  .fg{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
  .ff{display:flex;flex-direction:column;gap:5px;} .ff.full{grid-column:1/-1;}
  .fl{font-size:.7rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.06em;}
  .fi,.fta,.fsel{font-family:'Sora',sans-serif;font-size:.82rem;padding:9px 12px;border:1.5px solid #e0e4f0;border-radius:10px;background:#f8f9fc;color:#1a1d2e;outline:none;width:100%;}
  .fi:focus,.fta:focus,.fsel:focus{border-color:${accent};background:white;} .fta{resize:vertical;min-height:72px;}
  .warn-box{border-radius:10px;padding:10px 14px;font-size:.76rem;line-height:1.55;margin-bottom:14px;background:#fffbeb;border:1px solid #fde68a;color:#92400e;}
  .search-wrap{position:relative;flex:1;max-width:280px;}
  .search-wrap svg{position:absolute;left:10px;top:50%;transform:translateY(-50%);width:14px;height:14px;color:#9094a8;}
  .search-inp{width:100%;font-family:'Sora',sans-serif;font-size:.8rem;padding:8px 12px 8px 32px;border:1.5px solid #e0e4f0;border-radius:9px;outline:none;background:#f8f9fc;color:#1a1d2e;}
  .cards{display:none;padding:10px;}
  @media(max-width:768px){.fr-title{font-size:1.2rem;}.tbl-wrap{display:none!important;}.fg{grid-template-columns:1fr;}.ff.full{grid-column:1;}}
`;

export const createFinanceStaffRequestPage = ({
  apiBase,      // e.g. '/finance/librarians'
  addType,      // e.g. 'librarian_add'
  exitType,     // e.g. 'librarian_exit'
  reactivateType, // e.g. 'librarian_reactivate'
  targetRole,   // e.g. 'librarian' | 'hostel_member' — for the user_access request
  entityLabel, entityLabelPlural, accent,
  designationOptions = null,
}) => {
  const css = cssFor(accent);
  const emptyForm = { name:'', email:'', password:'', employeeId:'', mobile:'', cnic:'', address:'', joiningDate:'', ...(designationOptions ? { designation: designationOptions[0] } : {}) };

  return function FinanceStaffRequestPage() {
    const [members, setMembers] = useState([]);
    const hasLoaded = useRef(false);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [addModal, setAddModal] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [exitModal, setExitModal] = useState(null);
    const [exitForm, setExitForm] = useState({ exitDate:'', exitNote:'' });
    const [saving, setSaving] = useState(false);
    const [reactivateConfirm, setReactivateConfirm] = useState(null); // member
    const [reactivateSaving,  setReactivateSaving]  = useState(false);
    const [statusConfirm, setStatusConfirm] = useState(null); // { member, active }
    const [statusSaving,  setStatusSaving]  = useState(false);

    const load = async () => {
      if (!hasLoaded.current) setLoading(true);
      try {
        const { data } = await api.get(`${apiBase}?includeInactive=true`);
        hasLoaded.current = true;
        setMembers(data||[]);
      } catch { toast.error(`Failed to load ${entityLabelPlural.toLowerCase()}`); }
      finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const submitAdd = async (e) => {
      e.preventDefault();
      if (!form.name.trim() || !form.email.trim() || !form.password.trim()) { toast.error('Name, email and password are required'); return; }
      setSaving(true);
      try {
        await api.post('/finance/requests', { type: addType, payload: form, note: `Requesting new ${entityLabel.toLowerCase()}: ${form.name}` });
        toast.success('Request submitted for Admin approval');
        setAddModal(false); setForm(emptyForm);
      } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
      finally { setSaving(false); }
    };

    const submitExit = async () => {
      if (!exitForm.exitNote.trim()) { toast.error('Reason is required'); return; }
      setSaving(true);
      try {
        await api.post('/finance/requests', {
          type: exitType,
          payload: { userId: exitModal.member.userId, exitType: exitModal.exitType, exitDate: exitForm.exitDate, exitNote: exitForm.exitNote },
          note: `Requesting ${exitModal.exitType} for ${exitModal.member.name}`,
        });
        toast.success('Request submitted for Admin approval');
        setExitModal(null);
      } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
      finally { setSaving(false); }
    };

    const requestReactivate = (m) => setReactivateConfirm(m);

    const confirmReactivate = async () => {
      if (!reactivateConfirm) return;
      setReactivateSaving(true);
      try {
        await api.post('/finance/requests', { type: reactivateType, payload: { userId: reactivateConfirm.userId }, note: `Requesting reactivation for ${reactivateConfirm.name}` });
        toast.success('Request submitted for Admin approval');
        setReactivateConfirm(null);
        load();
      } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
      finally { setReactivateSaving(false); }
    };

    const openStatusConfirm = (m, active) => setStatusConfirm({ member: m, active });

    const confirmStatusAction = async () => {
      if (!statusConfirm) return;
      const { member, active } = statusConfirm;
      setStatusSaving(true);
      try {
        await api.post('/finance/requests', {
          type: 'user_access',
          payload: { targetId: member.userId, targetRole, active },
          note: `Requesting ${active ? 'activation' : 'deactivation'} for ${member.name}`,
        });
        toast.success('Request submitted for Admin approval');
        setStatusConfirm(null);
        load();
      } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
      finally { setStatusSaving(false); }
    };

    const filtered = members.filter(m => !search || (m.name||'').toLowerCase().includes(search.toLowerCase()));

    return (
      <>
        <style>{css}</style>
        <div className="fr">
          <div className="fr-hdr">
            <div><div className="fr-title">{entityLabelPlural}</div><div className="fr-sub">View-only — additions and exits go through Admin approval.</div></div>
            <button className="btn btn-accent" onClick={()=>setAddModal(true)}><PlusIcon/>Request New {entityLabel}</button>
          </div>
          <div className="panel">
            <div className="panel-hdr">
              <div className="search-wrap"><MagnifyingGlassIcon/><input className="search-inp" placeholder="Search by name…" value={search} onChange={e=>setSearch(e.target.value)}/></div>
            </div>
            {loading ? <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:10,padding:'48px',color:'#9094a8',fontSize:'.82rem'}}><span className="spinner"/>Loading…</div>
            : filtered.length === 0 ? <div className="empty">No {entityLabelPlural.toLowerCase()} found.</div>
            : (
              <div className="tbl-wrap" style={{overflowX:'auto'}}>
                <table className="tbl">
                  <thead><tr>
                    <th style={{minWidth:180}}>Name</th>
                    {designationOptions && <th style={{minWidth:120}}>Designation</th>}
                    <th style={{minWidth:100}}>Employee ID</th>
                    <th style={{minWidth:120}}>Mobile</th>
                    <th style={{minWidth:80}}>Status</th>
                    <th style={{textAlign:'right',minWidth:120}}>Actions</th>
                  </tr></thead>
                  <tbody>
                    {filtered.map(m => (
                      <tr key={m._id}>
                        <td><div className="nc"><div className="av">{ini(m.name)}</div><div><div className="nm">{m.name}</div><div className="ne">{m.email}</div></div></div></td>
                        {designationOptions && <td>{m.designation||'—'}</td>}
                        <td><span className="mono">{m.employeeId||'—'}</span></td>
                        <td><span className="mono">{m.mobile||'—'}</span></td>
                        <td><span className={`pill ${m.isActive ? 'active' : 'inactive'}`}>{m.isActive ? 'Active' : 'Inactive'}</span></td>
                        <td style={{textAlign:'right'}}>
                          {m.isActive ? <>
                            <button className="act-btn resign" title="Request Resignation" onClick={()=>{setExitModal({member:m,exitType:'resigned'}); setExitForm({exitDate:new Date().toISOString().split('T')[0],exitNote:''});}}><ArrowRightOnRectangleIcon/></button>
                            <button className="act-btn terminate" title="Request Termination" onClick={()=>{setExitModal({member:m,exitType:'terminated'}); setExitForm({exitDate:new Date().toISOString().split('T')[0],exitNote:''});}}><NoSymbolIcon/></button>
                            <button className="act-btn deactivate" title="Request Deactivate" onClick={()=>openStatusConfirm(m,false)}><LockClosedIcon/></button>
                          </> : m.exit_type ? (
                            <button className="act-btn reactivate" title="Request Reactivation" onClick={()=>requestReactivate(m)}><CheckCircleIcon/></button>
                          ) : (
                            <button className="act-btn activate" title="Request Activate" onClick={()=>openStatusConfirm(m,true)}><LockOpenIcon/></button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {addModal && createPortal(
          <div className="overlay" onClick={e=>e.target===e.currentTarget&&setAddModal(false)}>
            <div className="modal" style={{maxWidth:460}}>
              <div className="m-hdr"><div><div className="m-title">Request New {entityLabel}</div><div className="m-sub">Submitted to Admin for approval.</div></div><button className="m-close" onClick={()=>setAddModal(false)}><XMarkIcon/></button></div>
              <form onSubmit={submitAdd} style={{display:'contents'}}>
                <div className="m-body">
                  <div className="fg">
                    <div className="ff"><label className="fl">Full Name *</label><input className="fi" value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} required/></div>
                    <div className="ff"><label className="fl">Email *</label><input type="email" className="fi" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} required/></div>
                    <div className="ff full"><label className="fl">Password *</label><PasswordInput className="fi" autoComplete="new-password" value={form.password} onChange={e=>setForm(p=>({...p,password:e.target.value}))} required/></div>
                    {designationOptions && (
                      <div className="ff full"><label className="fl">Designation</label>
                        <select className="fsel" value={form.designation} onChange={e=>setForm(p=>({...p,designation:e.target.value}))}>
                          {designationOptions.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                      </div>
                    )}
                    <div className="ff"><label className="fl">Employee ID</label><input className="fi mono" value={form.employeeId} onChange={e=>setForm(p=>({...p,employeeId:e.target.value}))}/></div>
                    <div className="ff"><label className="fl">Mobile</label><input className="fi mono" placeholder="0300-1234567" value={form.mobile} onChange={e=>setForm(p=>({...p,mobile:e.target.value}))}/></div>
                    <div className="ff"><label className="fl">CNIC</label><input className="fi mono" placeholder="35201-1234567-1" value={form.cnic} onChange={e=>setForm(p=>({...p,cnic:e.target.value}))}/></div>
                    <div className="ff"><label className="fl">Joining Date</label><input type="date" className="fi" value={form.joiningDate} onChange={e=>setForm(p=>({...p,joiningDate:e.target.value}))}/></div>
                    <div className="ff full"><label className="fl">Address</label><input className="fi" placeholder="House/Street, City" value={form.address} onChange={e=>setForm(p=>({...p,address:e.target.value}))}/></div>
                  </div>
                </div>
                <div className="m-foot">
                  <button type="button" className="btn btn-ghost" onClick={()=>setAddModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-accent" disabled={saving}>{saving?'Submitting…':'Submit Request'}</button>
                </div>
              </form>
            </div>
          </div>, document.body)}

        {exitModal && createPortal(
          <div className="overlay" onClick={e=>e.target===e.currentTarget&&setExitModal(null)}>
            <div className="modal" style={{maxWidth:440}}>
              <div className="m-hdr"><div><div className="m-title">{exitModal.exitType==='resigned'?'Request Resignation':'Request Termination'}</div><div className="m-sub">{exitModal.member.name}</div></div><button className="m-close" onClick={()=>setExitModal(null)}><XMarkIcon/></button></div>
              <div className="m-body">
                <div className="warn-box">This submits a request to Admin — the account stays active until approved.</div>
                <div className="fg">
                  <div className="ff"><label className="fl">{exitModal.exitType==='resigned'?'Resignation Date':'Termination Date'}</label><input type="date" className="fi" value={exitForm.exitDate} onChange={e=>setExitForm(p=>({...p,exitDate:e.target.value}))}/></div>
                  <div className="ff full"><label className="fl">Reason</label><textarea className="fta" required value={exitForm.exitNote} onChange={e=>setExitForm(p=>({...p,exitNote:e.target.value}))} placeholder="Reason…"/></div>
                </div>
              </div>
              <div className="m-foot">
                <button className="btn btn-ghost" onClick={()=>setExitModal(null)}>Cancel</button>
                <button className={`btn ${exitModal.exitType==='resigned'?'btn-amber':'btn-red'}`} onClick={submitExit} disabled={saving}>{saving?'Submitting…':'Submit Request'}</button>
              </div>
            </div>
          </div>, document.body)}

        <ConfirmModal
          open={!!reactivateConfirm}
          title="Request Reactivation"
          subtitle={reactivateConfirm?.name}
          message={`Request reactivation for ${reactivateConfirm?.name}?`}
          confirmLabel="Request Reactivation"
          danger={false}
          onConfirm={confirmReactivate}
          onCancel={()=>setReactivateConfirm(null)}
          loading={reactivateSaving}
        />

        <ConfirmModal
          open={!!statusConfirm}
          title={statusConfirm?.active ? `Request Activate ${entityLabel}` : `Request Deactivate ${entityLabel}`}
          subtitle={statusConfirm?.member?.name}
          message={statusConfirm?.active
            ? `Request activation for ${statusConfirm?.member?.name}? Their login will be re-enabled once admin approves.`
            : `Request deactivation for ${statusConfirm?.member?.name}? They will be unable to log in once admin approves. This is reversible.`}
          confirmLabel={statusConfirm?.active ? 'Request Activate' : 'Request Deactivate'}
          danger={!statusConfirm?.active}
          onConfirm={confirmStatusAction}
          onCancel={()=>setStatusConfirm(null)}
          loading={statusSaving}
        />
      </>
    );
  };
};
